import { useEffect, useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  reportWeChatAuthFailure,
  type WeChatAuthFailureExtra,
  type WeChatAuthFailureStage,
} from './reportWeChatAuthFailure'
import { loadWxLoginScript, renderWxLoginQr } from './wxLoginScript'
import {
  isWeChatAuthUnavailableError,
  useWeChatAuthorize,
} from './useWeChatAuthorize'
import {
  WxLoginQrMountError,
  waitForWxLoginQrMount,
} from './waitForWxLoginQrMount'

type WeChatQrLoginButtonProps = {
  label: string
}

export function WeChatQrLoginButton({ label }: WeChatQrLoginButtonProps) {
  const { t } = useTranslation('auth')
  const containerId = useId().replace(/:/g, '')
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isLoadingQr, setIsLoadingQr] = useState(false)
  const { mutateAsync: authorizeWeChat } = useWeChatAuthorize()

  useEffect(() => {
    if (!isDialogOpen || !isLoadingQr) {
      return
    }

    let cancelled = false

    async function mountQrCode() {
      let stage: WeChatAuthFailureStage = 'authorize_api'
      let authorizeExtra: WeChatAuthFailureExtra | undefined
      try {
        const authorizeResponse = await authorizeWeChat({ intent: 'auto' })
        if (cancelled) {
          return
        }

        try {
          const redirectUrl = new URL(authorizeResponse.redirectUri)
          authorizeExtra = {
            appId: authorizeResponse.appId,
            redirectUriHost: redirectUrl.host,
            redirectUriPath: redirectUrl.pathname,
          }
        } catch {
          authorizeExtra = {
            appId: authorizeResponse.appId,
          }
        }

        stage = 'wx_script_load'
        await loadWxLoginScript()
        if (cancelled) {
          return
        }

        stage = 'wx_login_construct'
        renderWxLoginQr({
          containerId,
          appId: authorizeResponse.appId,
          redirectUri: authorizeResponse.redirectUri,
          state: authorizeResponse.state,
        })

        stage = 'wx_qr_mount'
        await waitForWxLoginQrMount(containerId)
      } catch (error) {
        if (error instanceof WxLoginQrMountError) {
          if (error.reason === 'widget_error_text') {
            stage = 'wx_qr_widget_error'
          }
        }

        reportWeChatAuthFailure({
          stage,
          intent: 'auto',
          error,
          extra: {
            ...authorizeExtra,
            ...(error instanceof WxLoginQrMountError
              ? { iframeSrcSummary: error.iframeSrcSummary }
              : {}),
          },
        })

        if (isWeChatAuthUnavailableError(error)) {
          toast.error(t('wechat.errors.unavailable'))
        } else if (
          stage === 'wx_qr_mount' ||
          stage === 'wx_qr_widget_error'
        ) {
          toast.error(t('wechat.qrLoadFailed'))
        } else {
          toast.error(t('wechat.errors.oauthFailed'))
        }
        setIsDialogOpen(false)
      } finally {
        if (!cancelled) {
          setIsLoadingQr(false)
        }
      }
    }

    void mountQrCode()

    return () => {
      cancelled = true
    }
  }, [authorizeWeChat, containerId, isDialogOpen, isLoadingQr, t])

  function handleOpenDialog() {
    setIsLoadingQr(true)
    setIsDialogOpen(true)
  }

  function handleDialogOpenChange(open: boolean) {
    setIsDialogOpen(open)
    if (!open) {
      setIsLoadingQr(false)
    }
  }

  return (
    <>
      <Button
        type="button"
        variant="outline"
        className="w-full"
        onClick={handleOpenDialog}
      >
        {label}
      </Button>
      <Dialog open={isDialogOpen} onOpenChange={handleDialogOpenChange}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{label}</DialogTitle>
            <DialogDescription>{t('wechat.scanQrDescription')}</DialogDescription>
          </DialogHeader>
          <div className="flex min-h-64 items-center justify-center">
            {isLoadingQr ? (
              <Loader2 className="size-8 animate-spin text-muted-foreground" />
            ) : null}
            <div id={containerId} className="flex justify-center" />
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
